import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, Divider, Grid, Input, InputAdornment, NativeSelect, Stack, Tooltip, Typography } from "@mui/material";
import {
  AccountBalanceWalletOutlined,
  AddCircleOutline,
  FactCheckOutlined,
  HistoryOutlined,
  LinkOutlined,
  PersonOutline,
  ReceiptLongOutlined,
  RefreshOutlined,
  RemoveCircleOutline,
} from "@mui/icons-material";
import Loader from "../../../UiComponents/Loader/Loader";
import MasterCommonTable from "../../../UiComponents/CommonTable/MasterCommonTable";
import { ToastError, ToastInfo, ToastSuccess } from "../../../UiComponents/Toaster/Toast";
// the Dashboard's own cards (owner approved its look; 2026-11-03: "Dashboard look" for both wallet pages)
import { KpiCard, TabbedCard } from "../../Dashboard/v2/parts";
import {
  checkRoadzenTopup,
  getRoadzenWalletStatement,
  getRoadzenWalletSummary,
  listRoadzenTopups,
  listRoadzenUsers,
} from "../../../Service/RoadzenService";
import AddAmountDialog from "./AddAmountDialog";
import StatementRowDialog from "./StatementRowDialog";
import { HistoryDialog } from "../RoadzenUsers/RoadzenUserDialogs";
import {
  countHint,
  defaultUserId,
  isSafeLink,
  ledgerCheck,
  mismatchWarning,
  rupeesText,
  topupStatusColour,
  topupStatusLabel,
  zenColour,
  zenProblem,
  transactionTypeOf,
  userOptionLabel,
  whenText,
} from "./roadzenWalletUtils";

// the Brisk Wallet pages' own header buttons, section titles and row buttons (Policy/BriskWallet)
const HEADER_BUTTON_SX = { height: "40px", gap: "8px", px: 2, whiteSpace: "nowrap" };
const SMALL_BUTTON_SX = { textTransform: "none", borderRadius: "6px", fontSize: 12, minWidth: 0, px: 1.25, py: 0.25, whiteSpace: "nowrap" };
const TABLE_CONFIG = { isAction: false, isSearch: false, isView: false, isEdit: false, isDelete: false, isMultiDelete: false, isDownload: false };
const ROWS_PER_PAGE = [10, 25, 50, 100];

/** A server-paged table's rows + its page (0-based) and size. */
const usePaged = () => {
  const [state, setState] = useState({ rows: [], total: 0, page: 0, limit: 25, loading: false, problem: "" });
  return [state, (patch) => setState((prev) => ({ ...prev, ...patch }))];
};

/**
 * RoadZen Wallet (owner, 2026-11-03), in the Brisk Wallet's look: one RoadZen user's RoadZen wallet. The user box opens
 * on the active user. The cards are RoadZen's balance and what its statement adds up to (checked to the paisa by the
 * server). "Recent transactions" is RoadZen's statement, "Payment links" its top-ups. Add Amount (test credit on the
 * sandbox, or a payment link) is for the ACTIVE user only; every CLIENT login may use it. RoadZen's wallet only.
 */
const RoadzenWallet = () => {
  const [users, setUsers] = useState(null);
  const [usersProblem, setUsersProblem] = useState("");
  const [apiEnv, setApiEnv] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryProblem, setSummaryProblem] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [statement, setStatement] = usePaged();
  const [topups, setTopups] = usePaged();
  const [checking, setChecking] = useState({}); // { [topup id]: true } while its status is read
  const [addOpen, setAddOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null); // a statement row opened by a click
  const [tab, setTab] = useState("transactions"); // the two tables are tabs (owner)

  // the users once (and on Refresh): the box opens on the active one
  useEffect(() => {
    let alive = true;
    listRoadzenUsers().then((res) => {
      if (!alive) return;
      if (res.error) {
        setUsersProblem(res.message);
        setUsers((prev) => prev || []);
        ToastError(res.message);
        return;
      }
      const list = res.data.users || [];
      setUsersProblem("");
      setUsers(list);
      setApiEnv(res.data.apiEnv || null);
      setSelectedId((prev) => (prev !== null && list.some((u) => u.id === prev) ? prev : defaultUserId(list)));
    });
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  // the chosen user's figures
  useEffect(() => {
    if (selectedId === null) return undefined;
    let alive = true;
    setSummaryLoading(true);
    getRoadzenWalletSummary(selectedId).then((res) => {
      if (!alive) return;
      setSummaryLoading(false);
      if (res.error) {
        setSummary(null);
        setSummaryProblem(res.message);
        return;
      }
      setSummaryProblem("");
      setSummary(res.data);
    });
    return () => {
      alive = false;
    };
  }, [selectedId, refreshKey]);

  // recent transactions (RoadZen's statement), one page
  useEffect(() => {
    if (selectedId === null) return undefined;
    let alive = true;
    setStatement({ loading: true });
    getRoadzenWalletStatement(selectedId, { page: statement.page + 1, limit: statement.limit }).then((res) => {
      if (!alive) return;
      if (res.error) return setStatement({ loading: false, rows: [], total: 0, problem: res.message });
      setStatement({ loading: false, problem: "", total: res.data.total || 0, rows: res.data.rows || [] });
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, statement.page, statement.limit, refreshKey]);

  // payment links, one page
  useEffect(() => {
    if (selectedId === null) return undefined;
    let alive = true;
    setTopups({ loading: true });
    listRoadzenTopups(selectedId, { page: topups.page + 1, limit: topups.limit }).then((res) => {
      if (!alive) return;
      if (res.error) return setTopups({ loading: false, rows: [], total: 0, problem: res.message });
      setTopups({ loading: false, problem: "", total: res.data.total || 0, rows: res.data.rows || [] });
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, topups.page, topups.limit, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const chooseUser = (value) => {
    setSelectedId(Number(value));
    setStatement({ page: 0, rows: [], total: 0 });
    setTopups({ page: 0, rows: [], total: 0 });
  };

  const checkStatus = async (row) => {
    setChecking((prev) => ({ ...prev, [row.id]: true }));
    const res = await checkRoadzenTopup(selectedId, row.id);
    setChecking((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
    if (res.error || (res.data && zenProblem(res.data.zen))) ToastError(res.message);
    else if (res.data && res.data.topup && res.data.topup.status === "PAID") ToastSuccess(res.message);
    else ToastInfo(res.message);
    refresh();
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      ToastSuccess("Payment link copied");
    } catch (e) {
      ToastError("Could not copy. Open the link and copy it from the address bar");
    }
  };

  const selected = (users || []).find((u) => u.id === selectedId) || null;
  const activeUsers = (users || []).filter((u) => u.isActive);
  const canAdd = Boolean(summary && summary.can && summary.can.add && selected && selected.isActive);
  const warning = mismatchWarning(summary);
  const check = ledgerCheck(summary);
  const addBlockedWhy = !selected
    ? ""
    : !selected.isActive
      ? activeUsers.length
        ? `Money can be added only to the active RoadZen user (${activeUsers.map((u) => u.name).join(", ")}).`
        : "No RoadZen user is active. Activate one on the RoadZen Users page first."
      : "";

  /* ---------------------------------------------------------------- Recent transactions (the Brisk ledger's columns) */
  const statementRows = statement.rows.map((r, i) => ({
    ...r,
    _id: r.id || String(i),
    dateText: whenText(r.createdAt),
    typeLabel: transactionTypeOf(r),
    addedByText: r.addedBy || "Not from this app",
    certificateText: r.certificateNumber || "-",
    noteText: r.note || "-",
    creditText: r.type === "CREDIT" ? rupeesText(r.amountPaise) : "",
    debitText: r.type === "DEBIT" ? rupeesText(r.amountPaise) : "",
    balanceText: rupeesText(r.balanceAfterPaise),
  }));
  const statementColumns = [
    { id: "dateText", label: "Transaction Date", placeMent: true, disablePadding: false },
    { id: "typeLabel", label: "Transaction Type", placeMent: true, disablePadding: false },
    // who added it, from our own records (owner: "table show who added that amount"); grey when made outside this app
    { id: "addedByText", label: "Added By", placeMent: true, disablePadding: false, type: "coloredText", getColor: (row) => (row.fromThisApp ? "#101828" : "#98A2B3") },
    { id: "certificateText", label: "Certificate No", placeMent: true, disablePadding: false },
    { id: "noteText", label: "Note", placeMent: true, disablePadding: false },
    { id: "creditText", label: "Credit", placeMent: true, disablePadding: false, type: "coloredText", getColor: () => "green" },
    { id: "debitText", label: "Debit", placeMent: true, disablePadding: false, type: "coloredText", getColor: () => "red" },
    { id: "balanceText", label: "Balance", placeMent: true, disablePadding: false },
  ];

  /* ---------------------------------------------------------------- Payment links (the Brisk Transaction History's columns) */
  const topupRows = topups.rows.map((r, i) => ({
    ...r,
    _id: r.id || String(i),
    dateText: whenText(r.createdAt),
    madeByText: r.madeBy || "-",
    amountText: rupeesText(r.amountPaise),
    statusText: topupStatusLabel(r.status),
    noteText: r.note || "-",
    expiresText: r.status === "PENDING" ? whenText(r.expiresAt) : "-",
    paidText: r.paidAt ? whenText(r.paidAt) : "-",
    methodText: r.paymentMethod || "-",
    providerText: r.providerPaymentId || "-",
  }));
  const topupColumns = [
    { id: "dateText", label: "Transaction Date", placeMent: true, disablePadding: false },
    { id: "madeByText", label: "User", placeMent: true, disablePadding: false },
    { id: "noteText", label: "Note", placeMent: true, disablePadding: false },
    { id: "statusText", label: "Status", placeMent: true, disablePadding: false, type: "coloredText", getColor: (row) => topupStatusColour(row.status) },
    { id: "amountText", label: "Amount", placeMent: true, disablePadding: false },
    { id: "expiresText", label: "Link Expires", placeMent: true, disablePadding: false },
    { id: "paidText", label: "Paid At", placeMent: true, disablePadding: false },
    { id: "methodText", label: "Payment Method", placeMent: true, disablePadding: false },
    { id: "providerText", label: "Payment Ref", placeMent: true, disablePadding: false },
    // what the agency's Zen Wallet got for the link (owner, 2026-11-04): only once RoadZen says it is paid
    {
      id: "zen",
      label: "Zen Wallet",
      placeMent: true,
      disablePadding: false,
      type: "component",
      // a short word in the state's colour, the agency under it (kept narrow: the table has no room to spare)
      renderComponent: (row) => (
        <Box data-testid="roadzen-link-zen" sx={{ maxWidth: 120 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: zenColour(row.zen), lineHeight: 1.3 }}>{(row.zen && row.zen.short) || "-"}</Typography>
          {row.zen && row.zen.agencyName && row.zen.state !== "NONE" && (
            <Typography sx={{ fontSize: 11.5, color: "#667085", lineHeight: 1.3, overflowWrap: "anywhere" }}>{row.zen.agencyName}</Typography>
          )}
        </Box>
      ),
    },
    {
      id: "action",
      label: "Action",
      placeMent: false,
      disablePadding: false,
      type: "component",
      renderComponent: (row) => {
        const link = row.status === "PENDING" && isSafeLink(row.paymentUrl) ? row.paymentUrl : null;
        return (
          // the buttons go to a second line only when the table has no room (it cannot scroll sideways)
          <Stack direction="row" useFlexGap sx={{ flexWrap: "wrap", gap: 1 }}>
            {link && (
              <Button size="small" variant="outlined" sx={SMALL_BUTTON_SX} onClick={() => copy(link)} aria-label={`Copy link ${row.amountText}`}>
                Copy link
              </Button>
            )}
            {link && (
              <Button size="small" variant="outlined" sx={SMALL_BUTTON_SX} onClick={() => window.open(link, "_blank", "noopener,noreferrer")} aria-label={`Open link ${row.amountText}`}>
                Open link
              </Button>
            )}
            <Button size="small" variant="outlined" sx={SMALL_BUTTON_SX} onClick={() => checkStatus(row)} disabled={Boolean(checking[row.id])} aria-label={`Check status ${row.amountText}`}>
              {checking[row.id] ? "Checking" : "Check status"}
            </Button>
          </Stack>
        );
      },
    },
  ];

  const pagedTable = (state, setState, rows, columns, onRowClick) => (
    <MasterCommonTable
      key={`${selectedId}`}
      data={rows}
      columns={columns}
      tableTotalCount={state.total}
      tableOutputEvent={(e) => {
        if (e.page !== undefined) setState({ page: e.page - 1 });
        if (e.limit) setState({ limit: e.limit });
      }}
      handleButton={{}}
      isServerSide={true}
      limit={state.limit}
      rowsPerPageOptions={ROWS_PER_PAGE}
      tableConfig={onRowClick ? { ...TABLE_CONFIG, onRowClick } : TABLE_CONFIG}
    />
  );

  return (
    <div style={{ margin: "1px" }}>
      {(users === null || (summaryLoading && !summary)) && <Loader />}
      <Grid container className="Master_Card_Container">
        <Grid container alignItems={"center"} justifyContent={"space-between"} className="Master_Card_Sub_Container" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography className="Master_Card_Tittle">
              RoadZen Wallet
              {apiEnv === "TEST" && <Chip size="small" color="warning" label="TEST" sx={{ ml: 1, verticalAlign: "middle" }} />}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={8} className="d-flex" justifyContent={{ xs: "start", sm: "flex-end", md: "flex-end" }}>
            <Stack direction={{ xs: "column", sm: "row", md: "row" }} spacing={2} mt={{ xs: 2, sm: 2, md: 0 }}>
              <Tooltip title={canAdd ? "" : addBlockedWhy} placement="bottom" disableInteractive>
                <span>
                  <Button
                    className="Master_Card_Filter_Button"
                    sx={HEADER_BUTTON_SX}
                    startIcon={<AccountBalanceWalletOutlined />}
                    onClick={() => setAddOpen(true)}
                    disabled={!canAdd}
                  >
                    Add Amount
                  </Button>
                </span>
              </Tooltip>
              <Button className="Master_Card_Filter_Button" sx={HEADER_BUTTON_SX} startIcon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>
                History
              </Button>
              <Button className="Master_Card_Filter_Button" sx={HEADER_BUTTON_SX} startIcon={<RefreshOutlined />} onClick={refresh} disabled={summaryLoading}>
                Refresh
              </Button>
            </Stack>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1, mb: 2 }} />
        </Grid>

        {(usersProblem || summaryProblem) && (
          <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
            {usersProblem || summaryProblem}
          </Alert>
        )}
        {warning && (
          <Alert severity="warning" sx={{ mb: 2, width: "100%" }}>
            {warning}
          </Alert>
        )}
        {addBlockedWhy && (
          <Alert severity="info" sx={{ mb: 2, width: "100%" }}>
            {addBlockedWhy} This user's wallet is shown below.
          </Alert>
        )}

        <Grid container spacing={1.5} sx={{ mb: 2 }} data-testid="roadzen-wallet-cards">
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              compact
              testId="roadzen-kpi-balance"
              label="RoadZen Balance"
              value={summary ? rupeesText(summary.balancePaise) : "—"}
              hint={selected ? `${selected.name}'s RoadZen wallet` : ""}
              icon={<AccountBalanceWalletOutlined sx={{ fontSize: 18 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              compact
              testId="roadzen-kpi-added"
              label="Total Added"
              value={summary ? rupeesText(summary.creditPaise) : "—"}
              hint={summary ? countHint(summary.creditCount, "credit", "credits") : ""}
              icon={<AddCircleOutline sx={{ fontSize: 18 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              compact
              testId="roadzen-kpi-used"
              label="Total Used"
              value={summary ? rupeesText(summary.debitPaise) : "—"}
              hint={summary ? countHint(summary.debitCount, "policy", "policies") : ""}
              icon={<RemoveCircleOutline sx={{ fontSize: 18 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              compact
              testId="roadzen-kpi-check"
              label="Ledger Check"
              value={check.value}
              hint={check.hint}
              accent={check.accent}
              icon={<FactCheckOutlined sx={{ fontSize: 18 }} />}
            />
          </Grid>
        </Grid>

        {/* the filter row (owner: "card want first and filter"): the RoadZen user whose wallet is shown */}
        <Grid container alignItems={"center"} className="Master_Card_Sub_Container" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <NativeSelect
              className="w-100"
              value={selectedId === null ? "" : String(selectedId)}
              onChange={(e) => chooseUser(e.target.value)}
              disabled={!users || !users.length}
              inputProps={{ id: "roadzen-wallet-user", "aria-label": "RoadZen user" }}
              input={
                <Input
                  className="w-100 Master_Card_Input"
                  disableUnderline={true}
                  startAdornment={
                    <InputAdornment position="start" sx={{ ml: 1, mr: 0.5 }}>
                      <PersonOutline sx={{ fontSize: "20px", color: "#667085" }} />
                    </InputAdornment>
                  }
                />
              }
            >
              {!users || !users.length ? <option value="">No RoadZen users</option> : null}
              {(users || []).map((u) => (
                <option key={u.id} value={String(u.id)}>
                  {userOptionLabel(u)}
                </option>
              ))}
            </NativeSelect>
          </Grid>
        </Grid>

        <TabbedCard
          testId="roadzen-wallet-tabs"
          value={tab}
          onChange={setTab}
          tabs={[
            { value: "transactions", label: "Recent transactions", count: statement.total, icon: <ReceiptLongOutlined sx={{ fontSize: 18 }} /> },
            { value: "links", label: "Payment links", count: topups.total, icon: <LinkOutlined sx={{ fontSize: 18 }} /> },
          ]}
          sx={{ width: "100%" }}
        >
          {tab === "transactions" ? (
            <>
              {statement.problem && (
                <Alert severity="error" sx={{ m: 1.5 }}>
                  {statement.problem}
                </Alert>
              )}
              {pagedTable(statement, setStatement, statementRows, statementColumns, setDetailRow)}
            </>
          ) : (
            <>
              {topups.problem && (
                <Alert severity="error" sx={{ m: 1.5 }}>
                  {topups.problem}
                </Alert>
              )}
              {pagedTable(topups, setTopups, topupRows, topupColumns)}
            </>
          )}
        </TabbedCard>
      </Grid>

      <AddAmountDialog
        open={addOpen}
        user={selected}
        testCreditAvailable={Boolean(summary && summary.can && summary.can.testCredit)}
        chooseAgency={Boolean(summary && summary.can && summary.can.chooseAgency)}
        onClose={() => setAddOpen(false)}
        onDone={refresh}
      />
      <StatementRowDialog row={detailRow} onClose={() => setDetailRow(null)} />
      <HistoryDialog kind="wallet" userId={selectedId} userName={selected ? selected.name : ""} open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </div>
  );
};

export default RoadzenWallet;
